import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";
import * as tradingBotService from "../services/tradingBotService";
import * as marketService from "../services/marketService";
import * as projectService from "../services/projectService";
import type { TradingBotConfig, UpdateTradingBotPayload } from "../types/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { useTranslation } from "react-i18next";
import { Pagination } from "../components/table/DataTable";

const BOT_ERROR_KEY_MAP: Record<string, string> = {
  "bot.error.noPositionToSell": "tradingBot.errorNoPositionToSell",
  "bot.error.klinesTimeout": "tradingBot.errorKlinesTimeout",
  "bot.error.signalGeneration": "tradingBot.errorSignalGeneration",
  "bot.error.amountTooLow": "tradingBot.errorAmountTooLow",
  "bot.error.invalidQuantity": "tradingBot.errorInvalidQuantity",
  "bot.error.orderCreation": "tradingBot.errorOrderCreation",
  // Legacy French strings (existing DB records)
  "Aucune position à vendre": "tradingBot.errorNoPositionToSell",
  "Timeout lors de la récupération des klines": "tradingBot.errorKlinesTimeout",
  "Erreur lors de la génération du signal": "tradingBot.errorSignalGeneration",
  "Montant maximum trop faible (< 10 USD)": "tradingBot.errorAmountTooLow",
  "Quantité calculée invalide": "tradingBot.errorInvalidQuantity",
  "Erreur lors de la création de l'ordre": "tradingBot.errorOrderCreation",
};

function translateBotError(
  msg: string | null,
  t: (key: string, opts?: Record<string, string>) => string
): string | null {
  if (!msg) return null;

  // Parameterized key: "bot.error.insufficientBalance|available|required"
  if (msg.startsWith("bot.error.insufficientBalance|")) {
    const [, available, required] = msg.split("|");
    return t("tradingBot.errorInsufficientBalance", { available, required });
  }

  // Legacy French dynamic string: "Solde insuffisant (X USD < Y USD)"
  const legacyMatch = msg.match(
    /^Solde insuffisant \((\d+(?:\.\d+)?) USD < (\d+(?:\.\d+)?) USD\)$/
  );
  if (legacyMatch) {
    return t("tradingBot.errorInsufficientBalance", {
      available: legacyMatch[1],
      required: legacyMatch[2],
    });
  }

  const i18nKey = BOT_ERROR_KEY_MAP[msg];
  return i18nKey ? t(i18nKey) : msg;
}

const FALLBACK_SYMBOLS = [
  "BTC/USD",
  "ETH/USD",
  "BNB/USD",
  "SOL/USD",
  "XRP/USD",
];
const STRATEGY_LABELS: Record<string, string> = {
  rsi: "RSI (survente/surachat)",
  ma_crossover: "Croisement de moyennes mobiles",
  macd: "MACD",
  bollinger: "Bollinger Bands",
};
const KLINE_INTERVALS = ["5m", "15m", "1h", "4h"];

type FormMode = "create" | "edit" | null;

const TradingBotPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const hasRobotAccess = Boolean(
    user && (user.role !== "client" || user.tradingBotAllowed)
  );
  const [configs, setConfigs] = useState<TradingBotConfig[]>([]);
  const [maxTradingBots, setMaxTradingBots] = useState<number>(1);
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [editingConfigId, setEditingConfigId] = useState<number | null>(null);
  const [defaults, setDefaults] = useState<
    Array<{
      type: string;
      label: string;
      params: Record<string, number | string>;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<UpdateTradingBotPayload>({
    enabled: false,
    strategyType: "rsi",
    symbol: "BTC/USD",
    params: {},
    maxPositionPct: 20,
    stopLossPct: null,
    takeProfitPct: null,
    minBalanceUSD: null,
  });
  const [history, setHistory] = useState<
    Array<{
      id: number;
      signal: "BUY" | "SELL" | null;
      orderId: number | null;
      symbol: string;
      strategyType: string;
      currentPrice: number | null;
      quantity: number | null;
      errorMessage: string | null;
      executionTimeMs: number | null;
      createdAt: string;
    }>
  >([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PAGE_SIZE = 20;
  const [availableSymbols, setAvailableSymbols] =
    useState<string[]>(FALLBACK_SYMBOLS);

  useEffect(() => {
    let isMounted = true;
    const loadSymbols = async () => {
      try {
        const [pairs, projects] = await Promise.all([
          marketService.getPopularPairs().catch(() => []),
          projectService.listProjects().catch(() => []),
        ]);
        if (!isMounted) return;
        const fromPairs = pairs.map((p) => p.symbol);
        const openProjects = projects.filter(
          (p: { status: string }) => p.status === "open"
        );
        const fromProjects: string[] = [];
        await Promise.all(
          openProjects.map(async (project: { symbol: string }) => {
            const symbol = `${project.symbol}/USD`;
            if (fromPairs.includes(symbol)) return;
            const exists = await marketService.checkSymbolExists(symbol);
            if (exists) fromProjects.push(symbol);
          })
        );
        const combined = [...new Set([...fromProjects, ...fromPairs])].sort();
        // Ne garder que les paires qui existent sur Binance (robot = ordres via Binance)
        const withBinanceCheck = await Promise.all(
          combined.map(async (symbol) => ({
            symbol,
            exists: await marketService.checkSymbolExists(symbol),
          }))
        );
        const binanceOnly = withBinanceCheck
          .filter(({ exists }) => exists)
          .map(({ symbol }) => symbol);
        if (!isMounted) return;
        setAvailableSymbols(
          binanceOnly.length > 0 ? binanceOnly : FALLBACK_SYMBOLS
        );
      } catch {
        if (isMounted) setAvailableSymbols(FALLBACK_SYMBOLS);
      }
    };
    void loadSymbols();
    return () => {
      isMounted = false;
    };
  }, []);

  const loadConfigs = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!user || !hasRobotAccess) return;
      const silent = opts?.silent ?? false;
      if (!silent) setLoading(true);
      try {
        const [configsRes, defaultsRes] = await Promise.all([
          tradingBotService.getConfigs(),
          tradingBotService.getDefaults(),
        ]);
        setConfigs(configsRes.configs ?? []);
        setMaxTradingBots(configsRes.maxTradingBots ?? 1);
        setDefaults(defaultsRes.strategies ?? []);
      } catch (err: any) {
        if (!silent) {
          toast({
            title: t("errors.generic", "Erreur"),
            description: err?.message ?? "Impossible de charger les robots",
            variant: "destructive",
          });
          setConfigs([]);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [user, toast, t]
  );

  const openCreateForm = () => {
    const strat = defaults[0];
    setForm({
      enabled: false,
      strategyType:
        (strat?.type as UpdateTradingBotPayload["strategyType"]) ?? "rsi",
      symbol: "BTC/USD",
      params: strat
        ? { ...strat.params }
        : { rsi_period: 14, rsi_buy: 30, rsi_sell: 70, kline_interval: "1h" },
      maxPositionPct: 20,
      stopLossPct: null,
      takeProfitPct: null,
      minBalanceUSD: null,
    });
    setFormMode("create");
    setEditingConfigId(null);
  };

  const openEditForm = (config: TradingBotConfig) => {
    setForm({
      enabled: config.enabled,
      strategyType: config.strategyType,
      symbol: config.symbol,
      params: { ...config.params },
      maxPositionPct: config.maxPositionPct,
      stopLossPct: config.stopLossPct ?? null,
      takeProfitPct: config.takeProfitPct ?? null,
      minBalanceUSD: config.minBalanceUSD ?? null,
    });
    setFormMode("edit");
    setEditingConfigId(config.id);
  };

  useEffect(() => {
    if (!hasRobotAccess) return;
    void loadConfigs(); // Premier chargement : spinner visible
    // Rafraîchir les configs en arrière-plan toutes les 30 s (sans spinner)
    const interval = setInterval(() => {
      void loadConfigs({ silent: true });
    }, 30000);
    return () => clearInterval(interval);
  }, [loadConfigs, hasRobotAccess]);

  const loadHistory = useCallback(
    async (opts?: { offset?: number; limit?: number; silent?: boolean }) => {
      if (!user || !hasRobotAccess) return;
      const offset = opts?.offset ?? historyPage * HISTORY_PAGE_SIZE;
      const limit = opts?.limit ?? HISTORY_PAGE_SIZE;
      const silent = opts?.silent ?? false;
      if (!silent) setHistoryLoading(true);
      try {
        const res = await tradingBotService.getHistory(limit, offset);
        setHistory(res.history ?? []);
        setHistoryTotal(res.total ?? 0);
      } catch (err: any) {
        if (!silent) {
          console.error("Erreur lors du chargement de l'historique:", err);
          toast({
            title: t("errors.generic", "Erreur"),
            description: err?.message ?? "Impossible de charger l'historique",
            variant: "destructive",
          });
          setHistory([]);
          setHistoryTotal(0);
        }
      } finally {
        if (!silent) setHistoryLoading(false);
      }
    },
    [user, hasRobotAccess, toast, t, historyPage]
  );

  // Premier chargement et changement de page : chargement visible
  useEffect(() => {
    if (!user) return;
    void loadHistory({
      offset: historyPage * HISTORY_PAGE_SIZE,
      limit: HISTORY_PAGE_SIZE,
      silent: false,
    });
  }, [user, historyPage, loadHistory]);

  // Rafraîchir l'historique en arrière-plan toutes les 30 s (sans spinner ni blocage)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      void loadHistory({
        offset: historyPage * HISTORY_PAGE_SIZE,
        limit: HISTORY_PAGE_SIZE,
        silent: true,
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [user, historyPage, loadHistory]);

  const handleSave = async () => {
    if (!user) return;
    try {
      setSaving(true);
      const payload: UpdateTradingBotPayload = {
        enabled: form.enabled,
        strategyType: form.strategyType,
        symbol: form.symbol,
        params: form.params,
        maxPositionPct: form.maxPositionPct,
        stopLossPct: form.stopLossPct ?? undefined,
        takeProfitPct: form.takeProfitPct ?? undefined,
        minBalanceUSD: form.minBalanceUSD ?? undefined,
      };
      if (formMode === "create") {
        await tradingBotService.createConfig({
          ...payload,
          strategyType: form.strategyType!,
        });
        toast({
          title: t("common.saved", "Enregistré"),
          description: t("tradingBot.created", "Robot créé."),
        });
      } else if (formMode === "edit" && editingConfigId != null) {
        await tradingBotService.updateConfig(editingConfigId, payload);
        toast({
          title: t("common.saved", "Enregistré"),
          description: form.enabled
            ? t(
                "tradingBot.savedEnabled",
                "Robot activé et paramètres enregistrés."
              )
            : t("tradingBot.saved", "Paramètres du robot enregistrés."),
        });
      }
      await Promise.all([loadConfigs({ silent: true }), loadHistory()]);
      setFormMode(null);
      setEditingConfigId(null);
    } catch (err: any) {
      toast({
        title: t("errors.generic", "Erreur"),
        description: err?.message ?? "Impossible d'enregistrer",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!user) return;
    if (!window.confirm(t("tradingBot.confirmDelete", "Supprimer ce robot ?")))
      return;
    try {
      await tradingBotService.deleteConfig(id);
      toast({
        title: t("common.saved", "Enregistré"),
        description: t("tradingBot.deleted", "Robot supprimé."),
      });
      await Promise.all([loadConfigs({ silent: true }), loadHistory()]);
      if (editingConfigId === id) {
        setFormMode(null);
        setEditingConfigId(null);
      }
    } catch (err: any) {
      toast({
        title: t("errors.generic", "Erreur"),
        description: err?.message ?? "Impossible de supprimer",
        variant: "destructive",
      });
    }
  };

  const updateParam = (key: string, value: number | string) => {
    setForm((prev) => ({
      ...prev,
      params: { ...prev.params, [key]: value },
    }));
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="container max-w-2xl mx-auto py-8 px-4">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (user?.role === "client" && !user?.tradingBotAllowed) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <h1 className="text-2xl font-semibold mb-2">
          {t("tradingBot.title", "Robot de trading")}
        </h1>
        <p className="text-muted-foreground rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          {t(
            "tradingBot.accessDenied",
            "L'accès au Robot n'est pas autorisé pour votre compte. Demandez à votre manager d'activer l'accès au Robot."
          )}
        </p>
      </div>
    );
  }

  const editingConfig =
    editingConfigId != null
      ? configs.find((c) => c.id === editingConfigId)
      : null;

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-semibold mb-2">
        {t("tradingBot.title", "Robot de trading")}
      </h1>
      <p className="text-muted-foreground mb-6">
        {t(
          "tradingBot.description",
          "Configurez un ou plusieurs robots qui exécutent des règles (RSI, MACD, etc.) et placent des ordres marché automatiquement."
        )}
      </p>

      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-medium">
          {t("tradingBot.myBots", "Mes robots")} ({configs.length})
        </h2>
        <div className="flex gap-2">
          {/* <Button
            variant="outline"
            onClick={async () => {
              try {
                const result = await tradingBotService.runMyBots();
                toast({
                  title: t("tradingBot.runCompleted", "Exécution terminée"),
                  description: t("tradingBot.runResult", "{{executed}} robot(s) exécuté(s), {{errors}} erreur(s)", {
                    executed: result.executed,
                    errors: result.errors,
                  }),
                });
                await Promise.all([loadConfigs({ silent: true }), loadHistory()]);
              } catch (err: any) {
                toast({
                  title: t("errors.generic", "Erreur"),
                  description: err?.message ?? "Impossible d'exécuter les robots",
                  variant: "destructive",
                });
              }
            }}
          >
            {t("tradingBot.runNow", "Exécuter maintenant")}
          </Button> */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={openCreateForm}
              disabled={configs.length >= maxTradingBots}>
              {t("tradingBot.addBot", "Ajouter un robot")}
            </Button>
            <span className="text-sm text-muted-foreground">
              {configs.length} / {maxTradingBots}{" "}
              {t("tradingBot.robots", "robots")}
            </span>
            {configs.length >= maxTradingBots && (
              <span className="text-xs text-muted-foreground">
                (
                {t(
                  "tradingBot.limitReached",
                  "Limite atteinte. Demandez à votre manager d'augmenter le nombre autorisé."
                )}
                )
              </span>
            )}
          </div>
        </div>
      </div>

      {configs.length > 0 && (
        <div className="flex flex-col gap-3 mb-8">
          {configs.map((c) => (
            <div
              key={c.id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border p-4 bg-card">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium shrink-0">{c.symbol}</span>
                  <span className="text-muted-foreground truncate">
                    {STRATEGY_LABELS[c.strategyType] ?? c.strategyType}
                  </span>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                    c.enabled
                      ? "bg-green-100 text-green-800"
                      : "bg-muted text-muted-foreground"
                  }`}>
                  {c.enabled
                    ? t("tradingBot.active", "Actif")
                    : t("tradingBot.disabled", "Inactif")}
                </span>
                {c.lastRunAt && (
                  <span className="text-sm text-muted-foreground shrink-0">
                    {t("tradingBot.lastRun", "Dernière exécution")}:{" "}
                    {new Date(c.lastRunAt).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditForm(c)}>
                  {t("common.edit", "Modifier")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => handleDelete(c.id)}>
                  {t("common.delete", "Supprimer")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formMode !== null && (
        <div className="space-y-6 rounded-lg border p-6 bg-card mb-8">
          <h3 className="font-medium">
            {formMode === "create"
              ? t("tradingBot.addBot", "Ajouter un robot")
              : t("tradingBot.editBot", "Modifier le robot")}
          </h3>
          <div className="space-y-2">
            {user?.role === "client" && !user?.tradingBotAllowed ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  "tradingBot.activationByManager",
                  "Demandez à votre manager d'autoriser l'accès au Robot pour pouvoir activer vos robots."
                )}
              </p>
            ) : (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="enabled"
                  checked={form.enabled ?? false}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, enabled: !!checked }))
                  }
                />
                <Label htmlFor="enabled" className="cursor-pointer">
                  {t("tradingBot.enabled", "Activer le robot")}
                </Label>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("tradingBot.strategy", "Stratégie")}</Label>
            <Select
              value={form.strategyType ?? "rsi"}
              onValueChange={(v) =>
                setForm((prev) => ({
                  ...prev,
                  strategyType: v as UpdateTradingBotPayload["strategyType"],
                  params: defaults.find((s) => s.type === v)?.params
                    ? { ...defaults.find((s) => s.type === v)!.params }
                    : prev.params,
                }))
              }>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rsi">
                  {STRATEGY_LABELS.rsi ?? t("tradingBot.strategyRsi", "RSI")}
                </SelectItem>
                <SelectItem value="ma_crossover">
                  {STRATEGY_LABELS.ma_crossover ??
                    t("tradingBot.strategyMa", "Croisement MA")}
                </SelectItem>
                <SelectItem value="macd">
                  {STRATEGY_LABELS.macd ?? t("tradingBot.strategyMacd", "MACD")}
                </SelectItem>
                <SelectItem value="bollinger">
                  {STRATEGY_LABELS.bollinger ??
                    t("tradingBot.strategyBollinger", "Bollinger Bands")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("tradingBot.symbol", "Symbole")}</Label>
            <Select
              value={form.symbol ?? availableSymbols[0] ?? "BTC/USD"}
              onValueChange={(v) =>
                setForm((prev) => ({ ...prev, symbol: v }))
              }>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableSymbols.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.strategyType === "rsi" && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>RSI période</Label>
                  <Input
                    type="number"
                    min={5}
                    max={50}
                    value={Number(form.params?.rsi_period) || 14}
                    onChange={(e) =>
                      updateParam(
                        "rsi_period",
                        parseInt(e.target.value, 10) || 14
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>RSI achat (≤)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={50}
                    value={Number(form.params?.rsi_buy) || 30}
                    onChange={(e) =>
                      updateParam("rsi_buy", parseInt(e.target.value, 10) || 30)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>RSI vente (≥)</Label>
                  <Input
                    type="number"
                    min={50}
                    max={100}
                    value={Number(form.params?.rsi_sell) || 70}
                    onChange={(e) =>
                      updateParam(
                        "rsi_sell",
                        parseInt(e.target.value, 10) || 70
                      )
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {t("tradingBot.klineInterval", "Intervalle des chandeliers")}
                </Label>
                <Select
                  value={String(form.params?.kline_interval ?? "1h")}
                  onValueChange={(v) => updateParam("kline_interval", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KLINE_INTERVALS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="mt-2 text-xs text-muted-foreground dark:text-white/50">
                {t(
                  "tradingBot.rsiTrendHelp",
                  "Note : la stratégie RSI utilise aussi une moyenne mobile longue (par défaut 200 périodes) comme filtre de tendance (BUY privilégiés au-dessus de la tendance, SELL en dessous)."
                )}
              </p>
            </>
          )}

          {form.strategyType === "ma_crossover" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>MA rapide</Label>
                  <Input
                    type="number"
                    min={2}
                    max={50}
                    value={Number(form.params?.ma_fast) || 7}
                    onChange={(e) =>
                      updateParam("ma_fast", parseInt(e.target.value, 10) || 7)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>MA lente</Label>
                  <Input
                    type="number"
                    min={5}
                    max={200}
                    value={Number(form.params?.ma_slow) || 25}
                    onChange={(e) =>
                      updateParam("ma_slow", parseInt(e.target.value, 10) || 25)
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {t("tradingBot.klineInterval", "Intervalle des chandeliers")}
                </Label>
                <Select
                  value={String(form.params?.kline_interval ?? "1h")}
                  onValueChange={(v) => updateParam("kline_interval", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KLINE_INTERVALS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {form.strategyType === "macd" && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>MACD rapide</Label>
                  <Input
                    type="number"
                    min={2}
                    max={50}
                    value={Number(form.params?.macd_fast) || 12}
                    onChange={(e) =>
                      updateParam(
                        "macd_fast",
                        parseInt(e.target.value, 10) || 12
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>MACD lente</Label>
                  <Input
                    type="number"
                    min={5}
                    max={50}
                    value={Number(form.params?.macd_slow) || 26}
                    onChange={(e) =>
                      updateParam(
                        "macd_slow",
                        parseInt(e.target.value, 10) || 26
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ligne de signal</Label>
                  <Input
                    type="number"
                    min={2}
                    max={20}
                    value={Number(form.params?.macd_signal) || 9}
                    onChange={(e) =>
                      updateParam(
                        "macd_signal",
                        parseInt(e.target.value, 10) || 9
                      )
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {t("tradingBot.klineInterval", "Intervalle des chandeliers")}
                </Label>
                <Select
                  value={String(form.params?.kline_interval ?? "1h")}
                  onValueChange={(v) => updateParam("kline_interval", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KLINE_INTERVALS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {form.strategyType === "bollinger" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Période</Label>
                  <Input
                    type="number"
                    min={5}
                    max={50}
                    value={Number(form.params?.bb_period) || 20}
                    onChange={(e) =>
                      updateParam(
                        "bb_period",
                        parseInt(e.target.value, 10) || 20
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Écart-type</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    step={0.5}
                    value={Number(form.params?.bb_std) || 2}
                    onChange={(e) =>
                      updateParam("bb_std", parseFloat(e.target.value) || 2)
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  {t("tradingBot.klineInterval", "Intervalle des chandeliers")}
                </Label>
                <Select
                  value={String(form.params?.kline_interval ?? "1h")}
                  onValueChange={(v) => updateParam("kline_interval", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KLINE_INTERVALS.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>
              {t(
                "tradingBot.maxPositionPct",
                "Position max. (% du solde trading)"
              )}
            </Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={form.maxPositionPct ?? 20}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  maxPositionPct: parseInt(e.target.value, 10) || 20,
                }))
              }
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t("tradingBot.stopLossPct", "Stop loss (%)")}</Label>
              <Input
                type="number"
                min={0}
                max={50}
                placeholder="—"
                value={form.stopLossPct != null ? form.stopLossPct : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    stopLossPct: v === "" ? null : parseInt(v, 10) || 0,
                  }));
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("tradingBot.takeProfitPct", "Take profit (%)")}</Label>
              <Input
                type="number"
                min={0}
                max={200}
                placeholder="—"
                value={form.takeProfitPct != null ? form.takeProfitPct : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    takeProfitPct: v === "" ? null : parseInt(v, 10) || 0,
                  }));
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("tradingBot.minBalanceUSD", "Solde min. (USD)")}</Label>
              <Input
                type="number"
                min={0}
                placeholder="—"
                value={form.minBalanceUSD != null ? form.minBalanceUSD : ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    minBalanceUSD: v === "" ? null : parseFloat(v) || 0,
                  }));
                }}
              />
            </div>
          </div>

          {formMode === "edit" && editingConfig && (
            <div className="text-sm text-muted-foreground border-t pt-4">
              {editingConfig.lastRunAt && (
                <p>
                  {t("tradingBot.lastRun", "Dernière exécution")}:{" "}
                  {new Date(editingConfig.lastRunAt).toLocaleString()}
                </p>
              )}
              {editingConfig.lastSignalAt && editingConfig.lastSignalSide && (
                <p>
                  {t("tradingBot.lastSignal", "Dernier signal")}:{" "}
                  {editingConfig.lastSignalSide} —{" "}
                  {new Date(editingConfig.lastSignalAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? t("common.saving", "Enregistrement…")
                : t("common.save", "Enregistrer")}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setFormMode(null);
                setEditingConfigId(null);
              }}>
              {t("common.cancel", "Annuler")}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-lg border p-6 bg-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {t("tradingBot.historyTitle", "Historique d'activité")}
          </h2>
          {/* <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void loadHistory({
                offset: historyPage * HISTORY_PAGE_SIZE,
                limit: HISTORY_PAGE_SIZE,
                silent: false,
              });
              void loadConfigs({ silent: true });
            }}
            disabled={historyLoading}
          >
            {historyLoading ? t("common.loading", "Chargement…") : t("common.refresh", "Rafraîchir")}
          </Button> */}
        </div>
        {historyLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : history.length === 0 && historyTotal === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("tradingBot.historyEmpty", "Aucune exécution enregistrée.")}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium">
                      {t("tradingBot.historyDate", "Date")}
                    </th>
                    <th className="text-left py-2 font-medium">
                      {t("tradingBot.historySignal", "Signal")}
                    </th>
                    <th className="text-left py-2 font-medium">
                      {t("tradingBot.historySymbol", "Symbole")}
                    </th>
                    <th className="text-left py-2 font-medium">
                      {t("tradingBot.historyStrategy", "Stratégie")}
                    </th>
                    <th className="text-right py-2 font-medium">
                      {t("tradingBot.historyPrice", "Prix")}
                    </th>
                    <th className="text-right py-2 font-medium">
                      {t("tradingBot.historyQty", "Qté")}
                    </th>
                    <th className="text-left py-2 font-medium">
                      {t("tradingBot.historyOrder", "Ordre")}
                    </th>
                    <th className="text-left py-2 font-medium">
                      {t("tradingBot.historyError", "Erreur")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b last:border-0">
                      <td className="py-2">
                        {new Date(h.createdAt).toLocaleString()}
                      </td>
                      <td className="py-2">
                        {h.signal === "BUY" && (
                          <span className="text-green-600 font-medium">
                            BUY
                          </span>
                        )}
                        {h.signal === "SELL" && (
                          <span className="text-red-600 font-medium">SELL</span>
                        )}
                        {!h.signal && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2">{h.symbol}</td>
                      <td className="py-2">{h.strategyType}</td>
                      <td className="py-2 text-right">
                        {h.currentPrice != null
                          ? h.currentPrice.toFixed(2)
                          : "—"}
                      </td>
                      <td className="py-2 text-right">
                        {h.quantity != null ? h.quantity.toFixed(6) : "—"}
                      </td>
                      <td className="py-2">{h.orderId ?? "—"}</td>
                      <td
                        className="py-2 text-muted-foreground max-w-[200px] truncate"
                        title={translateBotError(h.errorMessage, t) ?? undefined}>
                        {translateBotError(h.errorMessage, t) ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {historyTotal > 0 && (
              <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
                <Pagination
                  currentPage={historyPage + 1}
                  totalPages={Math.max(
                    1,
                    Math.ceil(historyTotal / HISTORY_PAGE_SIZE)
                  )}
                  totalItems={historyTotal}
                  itemsPerPage={HISTORY_PAGE_SIZE}
                  onPageChange={(page: number) => setHistoryPage(page - 1)}
                  isLoading={historyLoading}
                  ariaLabelPrevious={t("tradingBot.previousPage", "Précédent")}
                  ariaLabelNext={t("tradingBot.nextPage", "Suivant")}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TradingBotPage;
